import { useEffect } from 'react';
import { useLocalStorage } from '@mantine/hooks';
import { useLocalParticipant, useRoomContext } from '@livekit/components-react';
import { LocalAudioTrack, type Room } from 'livekit-client';
import { AudioContextNotRunningError, RnnoiseProcessor } from './rnnoise-processor';
import { i18n } from '@/i18n';
import { isLowPowerDevice } from '@/shared/client-utils';
import { notifyError } from '@/shared/notify';

const LIVEKIT_CLOUD_EDITION = 1;

let pendingChange = Promise.resolve();
let failureNotified = false;
let stopWaitingForGesture: (() => void) | undefined;

export function isLiveKitCloud(room: Room) {
    return Number(room.serverInfo?.edition) === LIVEKIT_CLOUD_EDITION;
}

export function useNoiseSuppressionPreference() {
    return useLocalStorage({
        key: 'meet.noiseSuppression',
        defaultValue: !isLowPowerDevice(),
        getInitialValueInEffect: false,
    });
}

function queueChange(change: () => Promise<void> | void) {
    pendingChange = pendingChange.then(change).catch((error: unknown) => {
        console.error(error);
    });
}

function retryAfterGesture(track: LocalAudioTrack) {
    stopWaitingForGesture?.();
    const controller = new AbortController();
    stopWaitingForGesture = () => {
        controller.abort();
        stopWaitingForGesture = undefined;
    };
    const retry = () => {
        stopWaitingForGesture?.();
        queueChange(() => enableNoiseSuppression(track, false));
    };
    document.addEventListener('pointerdown', retry, { capture: true, signal: controller.signal });
    document.addEventListener('keydown', retry, { capture: true, signal: controller.signal });
}

async function enableNoiseSuppression(track: LocalAudioTrack, canWaitForGesture: boolean) {
    if (track.getProcessor()) return;
    const processor: RnnoiseProcessor = new RnnoiseProcessor(() => {
        queueChange(async () => {
            if (track.getProcessor() !== processor) return;
            await track.stopProcessor();
            retryAfterGesture(track);
        });
    });
    try {
        await track.setProcessor(processor);
    } catch (error) {
        if (canWaitForGesture && error instanceof AudioContextNotRunningError) {
            retryAfterGesture(track);
            return;
        }
        if (failureNotified) {
            console.error(error);
            return;
        }
        failureNotified = true;
        notifyError(i18n.t('settings:microphone.noiseFilterFailed'), error);
    }
}

async function applyNoiseSuppression(track: LocalAudioTrack, enabled: boolean) {
    if (enabled) {
        await enableNoiseSuppression(track, true);
        return;
    }
    if (track.getProcessor() instanceof RnnoiseProcessor) await track.stopProcessor();
}

export function NoiseSuppression() {
    const room = useRoomContext();
    const { microphoneTrack } = useLocalParticipant();
    const [enabled] = useNoiseSuppressionPreference();
    const track = microphoneTrack?.track;

    useEffect(() => {
        if (!(track instanceof LocalAudioTrack) || isLiveKitCloud(room)) return;
        queueChange(() => applyNoiseSuppression(track, enabled));
        return () => {
            queueChange(() => stopWaitingForGesture?.());
        };
    }, [room, track, enabled]);

    return null;
}
